/**
 * JWT auth token storage and helpers for backend authentication.
 * Access token (short-lived) and refresh token (long-lived) are stored
 * so the axios interceptor can attach them and handle 401/refresh.
 */

const ACCESS_TOKEN_KEY = "twilanswer_access_token";
const REFRESH_TOKEN_KEY = "twilanswer_refresh_token";
const EXPIRES_IN_KEY = "twilanswer_expires_in";

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getExpiresIn() {
  return localStorage.getItem(EXPIRES_IN_KEY);
}

/**
 * Store tokens after login or refresh. Refresh tokens are rotated, so always
 * persist the new refresh_token from the response.
 */
export function setTokens({ access_token, refresh_token, expires_in }) {
  if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  if (expires_in != null) localStorage.setItem(EXPIRES_IN_KEY, String(expires_in));
}

/** Persist JWTs from an API response when access_token is present. */
export function setTokensFromResponse(data) {
  if (data?.access_token) {
    setTokens({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
    });
  }
}

/**
 * Clear tokens (e.g. on logout or when refresh fails with 401).
 */
export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EXPIRES_IN_KEY);
}

/**
 * Returns true if the request URL is a public endpoint that must not
 * send the Authorization header (no token yet or different auth).
 */
export function isPublicAuthUrl(url) {
  if (!url) return true;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const path = url.replace(base, "").split("?")[0];
  const publicPaths = [
    "get-user",           // login (email or Google)
    "auth/refresh",       // token refresh
    "user/change-password",
    "user/verify-invite", // invite page load (no auth)
    "get-user-by-email",
    "create-user",
    "user/create-just-user",
  ];
  return publicPaths.some((p) => path === p || path.endsWith("/" + p));
}
